// store/index.js
import { createStore } from 'vuex';
import VuexPersist from 'vuex-persist';

// Days a PAST_DUE subscription keeps access while Stripe retries payment (Subscription.PAST_DUE_GRACE_DAYS in the API).
const PAST_DUE_GRACE_DAYS = 14;

const vuexPersist = new VuexPersist({
  key: 'huntApply', // Key to store in localStorage
  storage: window.localStorage, // Use localStorage
});

const store = createStore({
  state() {
    return {
      isAuthenticated: false,
      loginExpires: null,
      user: null,
      drawPoints: null,
      savedHunts: [],
    };
  },
  mutations: {
    setAuthentication(state, payload) {
      state.isAuthenticated = payload.isAuthenticated;
      state.loginExpires = payload.loginExpires;
    },
    setUser(state, user) {
      state.user = user;
    },
    setDrawPoints(state, drawPoints) {
      state.drawPoints = drawPoints;
    },
    setSavedHunts(state, hunts) {
      state.savedHunts = hunts;
    },
    addSavedHunt(state, huntNameId) {
      console.log('Adding hunt to saved hunts:', huntNameId);
      if (!state.savedHunts.includes(huntNameId)) {
        state.savedHunts.push(huntNameId);
      }
    },
    removeSavedHunt(state, huntNameId) {
      console.log('Removing hunt from saved hunts:', huntNameId);
      state.savedHunts = state.savedHunts.filter(id => id !== huntNameId);
    }
  },
  actions: {
    login({ commit }, expirationTimeStamp) {
      commit('setAuthentication', { isAuthenticated: true, loginExpires: expirationTimeStamp });
    },
    logout({ commit }) {
      commit('setAuthentication', { isAuthenticated: false, loginExpires: null });
      commit('setUser', null);
      commit('setDrawPoints', null);
      commit('setSavedHunts', []);
    },
    setUser({ commit }, user) {
      commit('setUser', user);
    },
    setDrawPoints({ commit }, drawPoints) {
      commit('setDrawPoints', drawPoints);
    },
    async fetchSavedHunts({ commit }) {
      try {
        console.log('Fetching saved hunts...');
        const response = await fetch(process.env.VUE_APP_API_BASE_URL + '/api/saved-hunts', {
          method: 'GET',
          credentials: 'include'
        });
        if (response.ok) {
          const hunts = await response.json();
          console.log('Received saved hunts:', hunts);
          // Extract just the huntName IDs from the response to avoid serialization issues
          const huntIds = hunts.map(hunt => {
            console.log('Processing hunt:', hunt);
            return hunt.huntNameId;
          }).filter(id => id != null);
          commit('setSavedHunts', huntIds);
        } else {
          console.error('Failed to fetch saved hunts:', response.status, response.statusText);
          const text = await response.text();
          console.error('Response body:', text);
        }
      } catch (error) {
        console.error('Error fetching saved hunts:', error);
      }
    },
    async toggleSavedHunt({ commit, state }, huntNameId) {
      console.log('Current saved hunts:', state.savedHunts);
      console.log('Attempting to toggle hunt:', huntNameId);
      const isSaved = state.savedHunts.includes(huntNameId);
      try {
        const response = await fetch(process.env.VUE_APP_API_BASE_URL + `/api/saved-hunts/hunt/${huntNameId}`, {
          method: isSaved ? 'DELETE' : 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        if (response.ok) {
          if (isSaved) {
            console.log('Removing hunt from saved:', huntNameId);
            commit('removeSavedHunt', huntNameId);
          } else {
            console.log('Adding hunt to saved:', huntNameId);
            commit('addSavedHunt', huntNameId);
          }
        } else {
          console.error('Failed to toggle saved hunt:', response.status, response.statusText);
          const text = await response.text();
          console.error('Response body:', text);
        }
      } catch (error) {
        console.error('Error toggling saved hunt:', error);
      }
    }
  },
  getters: {
    isAuthenticated(state) {
      // Check if login has expired and update state accordingly
      if (!state.loginExpires || state.loginExpires < Date.now()) {
        if (state.isAuthenticated) {
          // If the state says authenticated but it's expired, update it
          state.isAuthenticated = false;
        }
        return false;
      }
      return state.isAuthenticated;
    },
    getUser(state) {
      return state.user;
    },
    getDrawPoints(state) {
      return state.drawPoints;
    },
    getSavedHunts(state) {
      return state.savedHunts;
    },
    hasActiveSubscription(state) {
      // Mirrors the API's Subscription.grantsAccess(): a cancelled or lapsed subscription is not Premium.
      const subscriptions = (state.user && state.user.subscriptions) || [];
      const now = Date.now();
      return subscriptions.some(sub => {
        const endDate = sub.endDate ? new Date(sub.endDate).getTime() : null;
        if (sub.status === 'ACTIVE') {
          return endDate == null || endDate > now;
        }
        if (sub.status === 'PAST_DUE') {
          return endDate != null && endDate + PAST_DUE_GRACE_DAYS * 24 * 60 * 60 * 1000 > now;
        }
        return false;
      });
    }
  },
  plugins: [vuexPersist.plugin],
});

export default store;
